import {
  type BuildDetailsFieldsParams,
  checkboxField,
  checklistField,
  field,
  formatAuditTrail,
  leftColumn,
  rightColumn,
  selectField,
  typedField,
  withLayout,
} from '../fieldHelpers';
import type { DetailField } from '../types';

type OpportunityInfoWithBrands = App.OpportunityInfo & {
  brands__c?: string;
};

type OfferWithSalesforceBrands = App.Offer & {
  sf_brands?: Array<string>;
};

function splitSalesforceMultiValue(value?: string | Array<string>) {
  const values = Array.isArray(value) ? value : (value ?? '').split(/[;,]/);

  return values.map((item) => item.trim()).filter(Boolean);
}

export function buildSystemInfoFields({
  offer,
  opportunity,
  opportunityId,
}: BuildDetailsFieldsParams): Array<DetailField> {
  const opportunityInfo = opportunity?.opportunity_info as OpportunityInfoWithBrands | undefined;
  const offerBrands = (offer as OfferWithSalesforceBrands | undefined)?.sf_brands;
  const brandOptions = splitSalesforceMultiValue(offerBrands ?? opportunityInfo?.brands__c);
  const brandsValue = brandOptions.join(';');
  const salesAndForecast = opportunity?.sales_and_forecast;
  const contractAndCommercial = opportunity?.contract_and_commercial;
  const dataCollection = opportunity?.data_collection;

  return [
    withLayout(field('18 Character ID', opportunityId, 'Salesforce-owned identifier.'), leftColumn),
    withLayout(
      field('Created By', formatAuditTrail(opportunityInfo?.created_by_name__c, opportunityInfo?.createddate)),
      leftColumn,
    ),
    withLayout(field('1st Payment Date', contractAndCommercial?.x1st_payment_date__c), leftColumn),
    withLayout(field('ClonedFromOpportunity', salesAndForecast?.clonedfromopportunity__c), leftColumn),
    withLayout(field('View Previous Deal in LE', undefined), leftColumn),
    withLayout(field('Days To Sign', salesAndForecast?.daystosign__c), leftColumn),
    typedField('Surcharge Duration', salesAndForecast?.surcharge_duration__c, 'number', undefined, leftColumn),
    withLayout(field('Total Duration Of the Opp', salesAndForecast?.total_duration_of_the_opp__c), leftColumn),
    withLayout(field('Tag', salesAndForecast?.tag__c), leftColumn),
    typedField('Previous TTV (AUD)', salesAndForecast?.previous_ttv__c, 'number', undefined, leftColumn),
    withLayout(
      field(
        'Last Modified By',
        formatAuditTrail(opportunityInfo?.last_modified_by_name__c, opportunityInfo?.lastmodifieddate),
      ),
      rightColumn,
    ),
    typedField(
      'RSM Approved Date',
      contractAndCommercial?.rsm_approved_date__c,
      'datetime-local',
      undefined,
      rightColumn,
    ),
    withLayout(field('Form Assembly Key', salesAndForecast?.form_assembly_key__c), rightColumn),
    withLayout(field('Approval Counter', salesAndForecast?.approval_counter__c), rightColumn),
    withLayout(field('Reopen Counter', salesAndForecast?.reopen_counter__c), rightColumn),
    withLayout(field('Surcharge % Coverage', salesAndForecast?.surcharge_coverage__c), rightColumn),
    withLayout(
      field(
        'Scrape ID',
        salesAndForecast?.scrape_id__c,
        'This field holds the ID of the Scrape so that it can be used in URLs.',
      ),
      rightColumn,
    ),
    withLayout(field('Performance Matrix', dataCollection?.performance_matrix__c), rightColumn),
    checklistField('Brands', brandsValue, brandOptions, undefined, rightColumn),
    checkboxField('RSM Approved', opportunityInfo?.rsm_approved__c, undefined, { ...rightColumn, inline: true }),
    checkboxField('Passed Validation', contractAndCommercial?.passed_validation__c, undefined, {
      ...rightColumn,
      inline: true,
    }),
    checkboxField('IsLocked', contractAndCommercial?.islocked__c, undefined, { ...rightColumn, inline: true }),
    typedField('Data Collection Start', dataCollection?.data_collection_start__c, 'datetime-local', undefined, {
      columns: 2,
      group: 'Documents Received',
    }),
    typedField('Data Collection Received', dataCollection?.data_collection_received__c, 'datetime-local', undefined, {
      columns: 2,
      group: 'Documents Received',
    }),
    selectField(
      'Data Collection Sheet Attached?',
      dataCollection?.data_collection_sheet_attached__c,
      ['Yes', 'No'],
      undefined,
      {
        columns: 2,
        group: 'Documents Received',
      },
    ),
    withLayout(field('OpptyId', opportunityId), { columns: 2, group: 'Documents Received' }),
    typedField(
      'Prop Info Collection Start',
      dataCollection?.prop_info_collection_start__c,
      'datetime-local',
      undefined,
      {
        columns: 2,
        group: 'Documents Received',
      },
    ),
    typedField('Property Info Received', dataCollection?.property_info_received__c, 'datetime-local', undefined, {
      columns: 2,
      group: 'Documents Received',
    }),
    withLayout(field('Project Key', salesAndForecast?.project_key__c), { columns: 2, group: 'Documents Received' }),
    selectField('Offer Ends In 7 Days', contractAndCommercial?.offer_ends_in_7_days__c, ['No', 'Yes'], undefined, {
      columns: 2,
      group: 'Documents Received',
    }),
  ];
}
